/**
 * API Error Handler with cross-tab single-flight token refresh.
 *
 * Two layers of deduplication:
 *
 *  1. Per-tab: a module-level `inFlightRefresh` promise so simultaneous 401s in the
 *     same tab queue behind one /refresh call.
 *  2. Cross-tab: `navigator.locks` serializes /refresh across all tabs of the same
 *     origin. Without this, two tabs that 401 simultaneously both fire /refresh with
 *     the same cookie, and the server's session-rotation step (which deletes the
 *     prior session) leaves one of the calls pointing at a row that no longer
 *     exists → 401 → forceLogout. The lock removes that race.
 *
 * Once a tab finishes a refresh it broadcasts the new access token via
 * BroadcastChannel so siblings can swap their in-memory token without each making
 * their own redundant round-trip.
 */

import {
  INVALID_CODE_ERROR,
  PENDING_BUSINESS_ID,
  SESSION_EXPIRED_ERROR,
} from '@mondaygpt/constants'
import { StatusCodes } from 'http-status-codes'
import { ApiError, OpenAPI } from 'openapi'
import store from 'store'
import { broadcastTokenRefreshed } from 'modules/authentication/utils/auth-broadcast'

interface RefreshResult {
  accessToken: string
}

type ApiFunction<T, Args extends any[]> = (...args: Args) => Promise<T>
type RejectWithValueFunction = Function | null

const LOG_PREFIX = '🔐 [TokenRefresh]'

const REFRESH_LOCK_NAME = 'taka-auth-refresh'

/**
 * The single in-flight refresh promise for this tab. Subsequent 401s wait on the same
 * promise instead of triggering parallel `/refresh` calls.
 */
let inFlightRefresh: Promise<void> | null = null

/**
 * Wraps the supplied refresh body with a cross-tab lock when the browser supports
 * the Web Locks API. Falls back to running the body unwrapped on browsers without
 * support (older Safari, etc.) — those tabs may still race, but that is a graceful
 * degradation: the worst case matches the no-coordination baseline.
 */
async function withCrossTabLock<T>(body: () => Promise<T>): Promise<T> {
  const locks = (
    navigator as Navigator & {
      locks?: { request: (name: string, cb: () => Promise<T>) => Promise<T> }
    }
  ).locks
  if (!locks?.request) return body()
  return locks.request(REFRESH_LOCK_NAME, body)
}

export const forceLogout = (): void => {
  console.warn(`${LOG_PREFIX} Performing forced logout due to authentication failure`)
  OpenAPI.TOKEN = undefined
  inFlightRefresh = null

  try {
    import('modules/authentication/slices/authentication-view.slice')
      .then(({ logoutAction }) => {
        store.dispatch(logoutAction())
      })
      .catch(error => {
        console.error(`${LOG_PREFIX} Error importing logoutAction:`, error)
      })
  } catch (error) {
    console.error(`${LOG_PREFIX} Error during logout:`, error)
  }
}

/**
 * Performs the single-flight token refresh. Subsequent callers awaiting the same
 * promise will all get the new token (or all reject) when it resolves.
 *
 * The refresh body runs inside a cross-tab Web Lock so concurrent tabs serialize
 * their /refresh calls, avoiding the rotation race that otherwise produces 401s
 * for the loser.
 */
const refreshOnce = (): Promise<void> => {
  if (inFlightRefresh) return inFlightRefresh

  // Snapshot the access token BEFORE we contend for the lock. If another tab
  // refreshes while we're waiting, its broadcast handler will mutate OpenAPI.TOKEN
  // to a new value, and we can detect that by comparing against this snapshot.
  const tokenBeforeLock = OpenAPI.TOKEN

  inFlightRefresh = (async () => {
    try {
      await withCrossTabLock(async () => {
        const slice = await import('modules/authentication/slices/authentication-view.slice')

        // Abort if a logout is in progress — refreshing now would create a new
        // server session that survives the logout (cookie + session resurrection).
        if (slice.isLoggingOut) {
          throw new Error('Refresh aborted: logout in progress')
        }

        // Inside the lock, check if a broadcast updated OpenAPI.TOKEN while we
        // waited. If so, the round-trip is unnecessary — siblings already minted
        // a fresh token for us.
        if (OpenAPI.TOKEN && OpenAPI.TOKEN !== tokenBeforeLock) {
          return
        }
        const { refreshTokenAction } = slice

        const businessId = store.getState().authenticationView.businessId ?? PENDING_BUSINESS_ID

        const result: RefreshResult = await store
          .dispatch(refreshTokenAction({ refreshToken: '', businessId }))
          .unwrap()

        if (!result?.accessToken) {
          throw new Error('Refresh response missing access token')
        }

        // OpenAPI.TOKEN is also set inside the slice extraReducer; setting it here keeps
        // the in-flight retry path correct even before the next render.
        OpenAPI.TOKEN = result.accessToken
        broadcastTokenRefreshed(result.accessToken)
      })
    } catch (error) {
      console.warn(`${LOG_PREFIX} Refresh failed:`, error)
      forceLogout()
      throw error
    } finally {
      inFlightRefresh = null
    }
  })()

  return inFlightRefresh
}

const handleAuthError = async <T, Args extends any[]>(
  error: any,
  apiFunction: ApiFunction<T, Args>,
  params: Args,
  rejectWithValue: RejectWithValueFunction
): Promise<T> => {
  try {
    await refreshOnce()
    return await apiFunction(...params)
  } catch (refreshError) {
    console.error(`${LOG_PREFIX} Token refresh failed:`, refreshError)
    return handleGenericError(error, rejectWithValue)
  }
}

const handleOtpError = (error: any, rejectWithValue: RejectWithValueFunction): any => {
  if (!rejectWithValue || typeof rejectWithValue !== 'function') throw error

  if (error instanceof ApiError) {
    return rejectWithValue(error.body)
  }

  return rejectWithValue({
    statusCode: StatusCodes.UNAUTHORIZED,
    message: INVALID_CODE_ERROR,
  })
}

const handleGenericError = (error: any, rejectWithValue: RejectWithValueFunction): any => {
  if (!rejectWithValue || typeof rejectWithValue !== 'function') throw error

  if (error instanceof TypeError) {
    return rejectWithValue({ statusCode: StatusCodes.BAD_REQUEST })
  }

  if (error instanceof ApiError) {
    return rejectWithValue(error.body)
  }

  return rejectWithValue(error)
}

const handleRejectWithValue = (
  error: any,
  rejectWithValue: RejectWithValueFunction,
  message: string
): any => {
  if (!rejectWithValue || typeof rejectWithValue !== 'function') throw error

  return rejectWithValue({
    statusCode: StatusCodes.UNAUTHORIZED,
    message,
  })
}

/**
 * Comprehensive error handler for API calls
 */
export const handleApiError = async function <T, Args extends any[]>(
  error: any,
  apiFunction: ApiFunction<T, Args>,
  params: Args,
  rejectWithValue: RejectWithValueFunction
): Promise<T> {
  if (!apiFunction || typeof apiFunction !== 'function') {
    console.error(`${LOG_PREFIX} Invalid API function provided to error handler`)
    return handleGenericError(error, rejectWithValue)
  }

  if (rejectWithValue && typeof rejectWithValue !== 'function') {
    console.error(`${LOG_PREFIX} Invalid rejectWithValue function provided to error handler`)
    throw error
  }

  // Extract status code and request URL (URL-based checks survive production minification)
  const statusCode = error instanceof ApiError ? error.status : error?.statusCode
  const requestUrl = error instanceof ApiError ? error.url : ''

  const isRefreshRequest = requestUrl.includes('/token/refresh')
  const isOtpVerification = requestUrl.includes('/otp/verify')

  if (isOtpVerification) {
    return handleOtpError(error, rejectWithValue)
  }

  if (statusCode === StatusCodes.UNAUTHORIZED && isRefreshRequest) {
    console.warn(`${LOG_PREFIX} Refresh token request failed, forcing logout`)
    forceLogout()
    return handleRejectWithValue(error, rejectWithValue, SESSION_EXPIRED_ERROR)
  }

  if (statusCode === StatusCodes.UNAUTHORIZED) {
    return await handleAuthError(error, apiFunction, params, rejectWithValue)
  }

  return handleGenericError(error, rejectWithValue)
}
